import axios from "axios";
import { put } from "redux-saga/effects";
import dayjs from "dayjs";
import {
  CallState,
  ExperiencesAvailabilityRequest,
  ExperiencesAvailabilityRequestEnum,
  ExperiencesAvailabilityResponse,
  ExperiencesAvailabilitySort,
  ExperiencesDestination,
  ExperiencesSelectionEnum,
  ActivitiesDestination,
  ActivitiesSelection,
  ActivitiesSelectionEnum,
  IIdActivities,
  IIdExperiences,
  IdEnum,
  ILocationQueryLabel,
  ILocationQueryCoordinate,
  LocationQueryEnum,
  IResponse,
  IResult,
} from "redmond";
import { actions } from "../actions";
import { config } from "../../../api/config";

const EXPERIENCES_AUTOCOMPLETE_PATH = "/api/v0/autocomplete/experiences";
const EXPERIENCES_AVAILABILITY_PATH = "/api/v0/experiences/availability";

const fetchActivityLocationAutocompleteByCoords = async (
  lat: number,
  lon: number
): Promise<IResponse> => {
  const body: ILocationQueryCoordinate = {
    LocationQuery: LocationQueryEnum.Coordinate,
    lat,
    lon,
  };
  const res = await axios.post(
    EXPERIENCES_AUTOCOMPLETE_PATH,
    { query: body },
    { baseURL: config.baseURL }
  );
  return res.data;
};

const fetchActivityLocationAutocompleteByLabel = async (
  label: string
): Promise<IResponse> => {
  const body: ILocationQueryLabel = {
    LocationQuery: LocationQueryEnum.Label,
    l: label,
  };
  const res = await axios.post(
    EXPERIENCES_AUTOCOMPLETE_PATH,
    { query: body },
    { baseURL: config.baseURL }
  );
  return res.data;
};

const fetchExperiencesAvailability = async (
  body: ExperiencesAvailabilityRequest
): Promise<ExperiencesAvailabilityResponse> => {
  const res = await axios.post(EXPERIENCES_AVAILABILITY_PATH, body, {
    baseURL: config.baseURL,
  });
  return res.data.value;
};

const getSelectionsFromResult = (
  result: IResult
): {
  experiencesSelection: ExperiencesDestination;
  activitiesSelection: ActivitiesSelection;
} | null => {
  const id = result.id;
  if (id.Id === IdEnum.Experiences) {
    const expSelection = (id as IIdExperiences)
      .experiencesSelection as unknown as ExperiencesDestination;
    const actSelection: ActivitiesDestination = {
      id: expSelection.id,
      name: expSelection.name,
      searchTerm: expSelection.searchTerm,
      ActivitiesSelection: ActivitiesSelectionEnum.ActivitiesDestination,
    };
    return {
      experiencesSelection: expSelection,
      activitiesSelection: actSelection,
    };
  }
  if (id.Id === IdEnum.Activities) {
    const actSelection = (id as IIdActivities)
      .activitiesSelection as unknown as ActivitiesDestination;
    const expSelection: ExperiencesDestination = {
      id: actSelection.id,
      name: actSelection.name,
      searchTerm: actSelection.searchTerm,
      ExperiencesSelection: ExperiencesSelectionEnum.ExperiencesDestination,
    };
    return {
      experiencesSelection: expSelection,
      activitiesSelection: actSelection,
    };
  }
  return null;
};

export function* fetchActivitiesXsellSaga(
  action: actions.IFetchActivitiesXsell
) {
  try {
    let locationResult: IResult | null = null;

    // A successful coordinate lookup that returns nothing is an authoritative
    // "no activities inventory near this hotel" — not a reason to retry by name.
    // Falling back on emptiness resolved same-named foreign cities (a hotel in
    // Alexandria, LA served Alexandria, Egypt tours). Only a genuine transport
    // failure justifies the name-based fallback.
    //
    // Tysons preserves the distinction: an activities-api success with zero
    // categories maps to HTTP 200 with an empty list, and only a gRPC-level
    // failure raises. So "resolved but empty" really does mean no destination.
    let coordinateLookupSucceeded = false;

    if (action.coords) {
      try {
        const res: IResponse = yield fetchActivityLocationAutocompleteByCoords(
          action.coords.lat,
          action.coords.lon
        );
        coordinateLookupSucceeded = true;
        locationResult = res.categories[0]?.results[0] ?? null;
      } catch {
        // Coordinate lookup failed outright; fall through to label lookup.
      }
    }

    if (!locationResult && !coordinateLookupSucceeded) {
      const res: IResponse = yield fetchActivityLocationAutocompleteByLabel(
        action.locationLabel
      );
      locationResult = res.categories[0]?.results[0] ?? null;
    }

    if (!locationResult) {
      yield put(actions.setActivitiesXsellCallState(CallState.Failed));
      return;
    }

    const selections = getSelectionsFromResult(locationResult);
    if (!selections) {
      yield put(actions.setActivitiesXsellCallState(CallState.Failed));
      return;
    }

    const requestBody: ExperiencesAvailabilityRequest = {
      AvailabilityRequest: ExperiencesAvailabilityRequestEnum.InitialSearch,
      ...(action.fromDate && action.untilDate
        ? {
            dateRange: {
              from: dayjs(action.fromDate).format("YYYY-MM-DD"),
              until: dayjs(action.untilDate).format("YYYY-MM-DD"),
            },
          }
        : {}),
      experiencesSelection: selections.experiencesSelection,
      activitiesSelection: selections.activitiesSelection,
      refinement: {
        sort: ExperiencesAvailabilitySort.Recommended,
        tags: [],
        duration: [],
        startTime: [],
      },
    };

    const response: ExperiencesAvailabilityResponse =
      yield fetchExperiencesAvailability(requestBody);

    yield put(
      actions.setActivitiesXsellResults(
        (response.experiences ?? []).slice(0, 6)
      )
    );
  } catch {
    yield put(actions.setActivitiesXsellCallState(CallState.Failed));
  }
}
